<script lang="ts" setup>
import {
  CoursePriceRange,
  CourseSortField,
  ListCoursesFilter,
  ListCoursesSort,
  ClubSortField,
  SortOrder
} from '__generated__/globalTypes'
import { listAmenities } from '__generated__/listAmenities'
import { listClubs } from '__generated__/listClubs'

import { ref, toRefs, watch } from 'vue'
import { useRouter, useRoute, onBeforeRouteUpdate } from 'vue-router'
import { useHead } from '@vueuse/head'

import { Card, CardType } from '@base/_types/Card'
import { useToastsStore } from '@base/store/toastsStore'
import { ToastSemantic } from '@base/_types/Toast'

import { LIST_CLUBS_QUERY } from '@base/api/queries/listClubs'
import { useClient } from '@base/api/useClient'
import { ugcImageSrc } from '@base/lib/ugcImageSrc'
import { createSlug } from '@base/lib/createSlug'
import { createDistanceContent } from '@base/lib/createDistanceContent'

import CardList from '@base/components/CardList/CardList.vue'
import ImageWrapper from '@base/components/ImageWrapper/ImageWrapper.vue'
import USHeroBanner from '@base/components/USHeroBanner/USHeroBanner.vue'
import USIcon from '@base/components/USIcon/USIcon.vue'
import USInputSort from '@base/components/USInputSort/USInputSort.vue'
import USPagination from '@base/components/USPagination/USPagination.vue'

import { formatCoursePriceRange } from '@base/helpers/formatCoursePriceRange'

import heroBannerImage from '@player/_assets/tee-times-cover.jpg'

import CourseSearchBar from '@player/_components/CourseSearchBar/CourseSearchBar.vue'

import { playerTitle } from '@player/_lib/playerTitle'

import { clubsRouteNames } from '@player/clubs/router/routeNames'
import { coursesRouteNames } from '@player/courses/router/routeNames'

import { LIST_AMENITIES } from '@player/courses/api/queries/listAmenities'

import { useCoursesStore } from '@player/courses/store/coursesStore'

import imageClubLogoPlaceholder from '@player/_assets/club-logo-placeholder.jpg'
import imageEventPlaceholder from '@player/_assets/event-placeholder.jpg'

import { props as propDefinition } from './props'

type CourseSearchBarSelectType = {
  label: string
  value: string
}

type CourseSearchEvent = {
  startDate: string | null
  endDate: string | null
  holes: string[] | undefined
  players: number | undefined
  lat: number | null
  lon: number | null
  loc: string | null
  price: CoursePriceRange[] | []
  clubs: string[] | []
  amenitiesValue: string[] | []
}

// Props
const props = defineProps(propDefinition)

// Static Variables
const limit = 10
const client = useClient()
const router = useRouter()
const route = useRoute()
const coursesStore = useCoursesStore()
const apiKey = import.meta.env.VITE_GCP_GEOCODING_API_KEY
const toastsStore = useToastsStore()

useHead({
  title: playerTitle('Courses')
})

const sortOptions: any[] = [
  {
    label: 'A-Z',
    value: CourseSortField.COURSE_NAME
  },
  {
    label: 'Near me',
    value: CourseSortField.DISTANCE
  },
  {
    label: 'Price Low to High',
    value: CourseSortField.PRICE_RANGE
  },
  /*
  {
    label: 'Reviews High to Low',
    value: CourseSortField.CLUB_REVIEWS
  }*/
]

// Inputs
const currentSort = ref<string>(CourseSortField.COURSE_NAME)
const currentPage = ref<number>(1)
const start = ref<Date | undefined>(undefined)
const end = ref<Date | undefined>(undefined)
const holes = ref<number | undefined>()
const players = ref<number | undefined>()
const lat = ref<number | null>()
const lon = ref<number | null>()
const loc = ref<string | null>()
const price = ref<CoursePriceRange[]>([])
const clubIds = ref<string[]>([])
const amenities = ref<string[]>([])
const loading = ref<boolean>(false)
const cards = <Card[]>[]

const { sort } = toRefs(props)

currentSort.value = sort?.value || CourseSortField.COURSE_NAME
currentPage.value = props.page || 1
start.value = props.start ? new Date(props.start) : undefined
end.value = props.end ? new Date(props.end) : undefined
holes.value = props.holes ? props.holes : undefined
players.value = props.players ? props.players : undefined
lat.value = props.lat ? props.lat : undefined
lon.value = props.lon ? props.lon : undefined
loc.value = props.loc || undefined
price.value = props.price ? props.price.split(',') : []
clubIds.value = props.clubs ? props.clubs.split(',') : []
amenities.value = props.amenities ? props.amenities.split(',') : []

// Outputs
const { courses, count, serverError } = toRefs(coursesStore)
const maxPage = ref<number>(1)
const usedClubIds = ref<string[]>([])
const clubData = ref<CourseSearchBarSelectType[]>([])
const clubAmenities = ref<CourseSearchBarSelectType[]>([])
const results = ref<string>('')

const courseSearchBarProps = {
  apiKey: apiKey,
  buttonLabel: 'Search',
  amenitiesTypeSelect: clubAmenities.value,
  golfClubSelect: clubData.value,
  showAdvanced: false,
  price: price.value,
  holes: holes.value,
  players: players.value,
  clubs: clubIds.value,
  amenities: amenities.value,
  startDate: start.value,
  endDate: end.value,
  lat: lat.value,
  lon: lon.value,
  loc: loc.value
}

// Helper Functions
const reset = () => {
  serverError.value = false
  courses.value = []
  count.value = 0
  results.value = ''
  loading.value = false
}

const handleCount = () => {
  results.value = ''
  let minResults = 0
  let maxResults = 0
  if (count.value > 0) {
    if (currentPage.value - 1 > 0) {
      minResults = (currentPage.value - 1) * limit
    } else {
      minResults = currentPage.value - 1
    }

    if (count.value < currentPage.value * limit) {
      maxResults = count.value
    } else {
      maxResults = Math.ceil(currentPage.value * limit)
    }
    results.value += `${minResults} - ${maxResults}`
  }

  results.value += ` of ${count.value}`
}

const handleSort = (e: [string]) => {
  currentPage.value = 1
  if (e[0] === CourseSortField.COURSE_NAME) {
    currentSort.value = CourseSortField.COURSE_NAME
    router.replace({
      query: { ...route.query, sort: undefined },
      params: { page: undefined }
    })
  } else if (e[0] === CourseSortField.DISTANCE) {
    currentSort.value = CourseSortField.DISTANCE
    router.replace({
      query: { ...route.query, sort: CourseSortField.DISTANCE },
      params: { page: undefined }
    })
  } else if (e[0] === CourseSortField.PRICE_RANGE) {
    currentSort.value = CourseSortField.PRICE_RANGE
    router.replace({
      query: { ...route.query, sort: CourseSortField.PRICE_RANGE },
      params: { page: undefined }
    })
  } else if (e[0] === CourseSortField.CLUB_REVIEWS) {
    currentSort.value = CourseSortField.CLUB_REVIEWS
    router.replace({
      query: { ...route.query, sort: CourseSortField.CLUB_REVIEWS },
      params: { page: undefined }
    })
  }
}

const handlePagination = (page: number) => {
  currentPage.value = page
  router.push({ params: { page: page }, query: router.currentRoute.value.query })
}

const generateClubLogo = (logo: string, clubName: string) => {
  return {
    src: logo ? ugcImageSrc(logo || '')[0] : imageClubLogoPlaceholder,
    srcset: logo ? ugcImageSrc(logo || '')[0] : imageClubLogoPlaceholder,
    alt: `The logo for ${clubName}`
  }
}

const generateCourseFeaturedImage = (
  clubName: string,
  courseName: string,
  featuredImage: string[]
) => {
  return {
    src:
      featuredImage && featuredImage[0]
        ? ugcImageSrc(featuredImage[0] || '')[0]
        : imageEventPlaceholder,
    srcset:
      featuredImage && featuredImage[0]
        ? ugcImageSrc(featuredImage[0] || '')[0]
        : imageEventPlaceholder,
    alt: `The featuredImage for the ${courseName} course at ${clubName}`
  }
}

const handleCourseSearch = (e: CourseSearchEvent) => {

  const query: any = {
    sort: strToSort(currentSort.value)[0]
  }

  // If a location was selected via the ClubSearchBar, let's set our lat/lon/loc
  // values and add the data to the querystring
  if (e.lat && e.lon && e.loc) {
    lat.value = e.lat
    lon.value = e.lon
    loc.value = e.loc

    query.lat = e.lat
    query.lon = e.lon
    query.loc = e.loc

    // If a location was selected, let's set the sort to DISTANCE
    query.sort = CourseSortField.DISTANCE
    currentSort.value = CourseSortField.DISTANCE
  }

  const fmtStart = e.startDate ? new Date(e.startDate) || undefined : undefined
  if (fmtStart) fmtStart.setHours(0, 0, 0)
  start.value = fmtStart
  query.start = start.value

  let fmtEnd = e.endDate ? new Date(e.endDate) || undefined : undefined
  if (fmtEnd) fmtEnd.setHours(23, 59, 59)
  end.value = fmtEnd
  query.end = end.value

  holes.value = e.holes?.length && e.holes[0] ? parseInt(e.holes[0]) : undefined
  query.holes = holes.value

  players.value = e.players
  query.players = players.value

  price.value = e.price
  query.price = price.value

  clubIds.value = e.clubs
  query.clubs = clubIds.value

  amenities.value = e.amenitiesValue
  query.amenities = amenities.value

  currentPage.value = 1
  router.push({ query, params: { page: undefined} })
}

const formatCourseCards = () => {
  while(cards.length > 0) { cards.pop() }
  courses.value.forEach((course) => {
    if (
      course.club.__typename === 'GetClubOutput' &&
      course.club.club !== null
    ) {
      let clubLogo = generateClubLogo(
        course.club.club.logo || '',
        course.club.club.clubName
      )

      let courseFeaturedImage = generateCourseFeaturedImage(
        course.club.club.clubName,
        course.courseName,
        course.gallery || []
      )

      let formattedCoursePriceRange = course.priceRange
        ? formatCoursePriceRange(course.priceRange)
        : 'Inquire'

      let setCourseStatus = course.open
        ? 'Open for season'
        : 'Closed for season'

      cards.push({
        type: CardType.CardListingCourse,
        data: {
          club: {
            name: course.club.club.clubName,
            rating: course.club.club.averageRating || 0,
            logo: clubLogo,
            to: {
              name: clubsRouteNames.ClubSingle,
              params: {
                slug: createSlug(
                  course.club.club.clubName,
                  course.club.club.slug
                )
              }
            }
          },
          course: {
            name: course.courseName,
            holes: course.numHoles || 0,
            par: course.par || 0,
            price: formattedCoursePriceRange,
            featuredImage: courseFeaturedImage,
            status: setCourseStatus,
            location: course.club.club.city || '',
            distance:
              course.club.club.addressGeom && lat.value && lon.value
                ? createDistanceContent(course.club.club.addressGeom, {
                    lat: lat.value,
                    lon: lon.value
                  })
                : '',
            to: {
              teeTimes: {
                name: coursesRouteNames.CourseSingle,
                params: {
                  slug: createSlug(course.courseName, course.slug)
                }
              }
            }
          }
        }
      })
    }
  })
}

const strToSort = (str: string | undefined): [CourseSortField, SortOrder] => {
  if (str === CourseSortField.COURSE_NAME) {
    return [CourseSortField.COURSE_NAME, SortOrder.ASC]
  } else if (str === CourseSortField.PRICE_RANGE) {
    return [CourseSortField.PRICE_RANGE, SortOrder.ASC]
  } else if (str === CourseSortField.DISTANCE) {
    return [CourseSortField.DISTANCE, SortOrder.ASC]
  } else if (str === CourseSortField.CLUB_REVIEWS) {
    return [CourseSortField.CLUB_REVIEWS, SortOrder.ASC]
  }
  return [CourseSortField.COURSE_NAME, SortOrder.ASC]
}

// Query Functions

const fetchGolfClubs = async () => {
  try {
    let { data } = await client.query<listClubs>({
      query: LIST_CLUBS_QUERY,
      variables: {
        filter: {},
        sortField: ClubSortField.NAME,
        sortOrder: SortOrder.ASC,
        offset: 0,
        limit: 0
      },
      fetchPolicy: 'cache-first'
    })

    if (data.listClubs.__typename === 'ListClubsOutput') {
      data.listClubs.clubs.forEach((club) => {
        if (!usedClubIds.value.includes(club.id)) {
          usedClubIds.value.push(club.id)
          clubData.value.push({
            label: club.clubName,
            value: club.id
          })
        }
      })
    }
  } catch (e) {
    console.error(e)
    serverError.value = true
  }
}

const fetchListOfAmenities = async () => {
  try {
    const { data } = await client.query<listAmenities>({
      query: LIST_AMENITIES,
      fetchPolicy: 'cache-first'
    })

    if (!data || data.fetchAmenities.__typename !== 'AttributesOutput') {
      return
    }

    data.fetchAmenities.attributes.forEach((item) => {
      clubAmenities.value.push({
        label: item.name,
        value: item.id
      })
    })
/*
    let { data: listClubsData } = await client.query<listClubs>({
      query: LIST_CLUBS_QUERY,
      variables: {
        filter: {},
        sortField: ClubSortField.NAME,
        sortOrder: SortOrder.ASC,
        offset: 0,
        limit: 0
      },
      fetchPolicy: 'cache-first'
    })

    if (listClubsData.listClubs.__typename === 'ListClubsOutput') {
      listClubsData.listClubs.clubs.forEach((club) => {
        if (!usedClubIds.value.includes(club.id)) {
          usedClubIds.value.push(club.id)
          clubData.value.push({
            label: club.clubName,
            value: club.id
          })
        }
      })
    }
      */
  } catch (e) {
    console.error(e)
    serverError.value = true
  }
}

const fetchListOfClubs = async () => {
  try {
    let { data } = await client.query<listClubs>({
      query: LIST_CLUBS_QUERY,
      variables: {
        filter: {},
        sortField: ClubSortField.NAME,
        sortOrder: SortOrder.ASC,
        offset: 0,
        limit: 0
      },
      fetchPolicy: 'cache-first'
    })

    if (data.listClubs.__typename === 'ListClubsOutput') {
      data.listClubs.clubs.forEach((club) => {
        if (!usedClubIds.value.includes(club.id)) {
          usedClubIds.value.push(club.id)
          clubData.value.push({
            label: club.clubName,
            value: club.id
          })
        }
      })
    }
  } catch (e) {
    console.error(e)
    serverError.value = true
  }
}

const fetchData = async () => {
  reset()
  loading.value = true
  try {

    const [sortField, sortOrder] = strToSort(currentSort.value)

    // If we already have lat/lon values (from the navigator or from a location search)
    // simply proceed with our query.
    if (lat.value && lon.value) {
      // Do nothing
    }

    // If the location data has been provided via the querystring, let's set our
    // lat/lon variables and proceed
    else if (route.query.lat && route.query.lon) {
      lat.value = props.lat
      lon.value = props.lon
    }

    // If we do not have lat/lon values yet, grab them from the navigator
    else {
      const navigatorPromise = new Promise((resolve, reject) => {
        if (navigator && navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              lat.value = position.coords.latitude
              lon.value = position.coords.longitude
              resolve(true);
            },
            () => {
              console.error('user declined')
              reject()
            }
          )
        } else {
          toastsStore.pushToast({
            content: 'Your browser dose not support geolocation',
            icon: 'exclamation-square',
            semantic: ToastSemantic.Error
          })
          reject()
        }
      })

      // If the current sort is by distance, let's wait for the navigator promise
      // to resolve so that the sort works properly. If not, we can let that happen
      // asynchronously behind the scenes. This should only happen when the user 
      // navigates directly to a copied URL with the sort in the query string.
      if (sortField === CourseSortField.DISTANCE) {
        await navigatorPromise;
      }
    }

    const filter: ListCoursesFilter = {
      availability: players.value || undefined,
      startDate: start.value,
      endDate: end.value,
      includeDeactivated: false,
      numHoles: holes.value,
      addressGeom:
        lat.value !== undefined &&
        lat.value !== null &&
        lon.value !== undefined &&
        lon.value !== null
          ? { lat: lat.value, lon: lon.value }
          : undefined,
      priceRange: price.value,
      clubIds: clubIds.value,
      amenities: amenities.value
    }

    const sort: ListCoursesSort[] = [
      {
        sortField: sortField,
        sortOrder: sortOrder
      }
    ]

    const offset = (currentPage.value - 1) * limit
    await coursesStore.listCourses(
      client,
      filter,
      sort,
      offset,
      limit,
      start.value?.toISOString() ||
        new Date(new Date().setHours(0, 0, 0)).toISOString(),
      end.value?.toISOString()
    )

    if (serverError.value === true) return
    maxPage.value = Math.ceil(count.value / limit)
    handleCount()
    formatCourseCards()
  } catch (e) {
    console.error(e)
    serverError.value = true
  } finally {
    loading.value = false
  }
}

// Fetch Data when the URL changes the props

onBeforeRouteUpdate(async () => {
  await fetchData()
})

// Fetch data when the view is first rendered
await fetchListOfAmenities()
//await fetchListOfClubs()
fetchData()
</script>

<template>
  <div class="courses">
    <USHeroBanner class="courses__hero-banner">
      <div class="overlay"></div>
      <ImageWrapper :src="heroBannerImage" object-fit="cover" height="100%" />
      <div class="courses__hero-banner-content">
        <div class="grid__row">
          <div class="grid__column grid__column--12">
            <div class="font-color--neutral-a5 text-align--center">
              <p class="display--d1">Find Your Course</p>
            </div>
          </div>
        </div>
      </div>
    </USHeroBanner>
    <div class="grid__row courses__row-containing-searchbar">
      <div class="grid__column grid__column--12 grid__column--breakpoint--13--10 grid__column--breakpoint--13--offset--1">
        <div class="courses__searchbar-wrapper">
          <CourseSearchBar
            v-bind="courseSearchBarProps"
            @submitCourseSearch="handleCourseSearch($event)"
          />
        </div>
      </div>
    </div>
    <template v-if="loading">
      <div class="grid__row">
        <div class="grid__column grid__column--12">
          <USIcon name="spinner" css-diameter="22px" class="courses__loading" />
        </div>
      </div>
    </template>
    <template v-else-if="courses && courses.length">
      <div class="grid__row">
        <div class="grid__column grid__column--12">
          <div class="courses__listing-controls margin-top--6 margin-bottom--8">
            <p class="body--p2--regular font-color--neutral-a3">
              {{ results }}
            </p>
            <USInputSort
              :model-value="[currentSort]"
              select-text="Sort"
              :options="sortOptions"
              @update:model-value="handleSort"
            />
          </div>
        </div>
      </div>
      <CardList :cards="cards" />
      <div>
        <USPagination
          v-if="maxPage > 1"
          :min="1"
          :max="maxPage"
          :current="currentPage"
          class="margin-top--5"
          @page-goto="handlePagination"
          @page-next="handlePagination"
          @page-previous="handlePagination"
        />
      </div>
    </template>
    <template v-else>
      <div class="grid__row">
        <div class="grid__column grid__column--12">
          <p class="body-p1--regular text-align--center margin-top--6">
            Hmmm! we don't seem to have any courses that meet your search
            criteria. Try another search.
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';

$search-bar-offset-sm: 30px;
$search-bar-offset-lg: 50px;

.courses {
  &__hero-banner {
    margin-top: calc(-1 * var(--spacing--5));

    .overlay {
      z-index: 1;
      position: absolute;
      width: 100%;
      height: 100%;
      background-color: black;
      opacity: .15;
    }
  }

  &__hero-banner-content {
    z-index: 2;
    position: absolute;
    left: 0;
    top: calc(50%);
    transform: translateY(-50%);
    width: 100%;
  }

  &__row-containing-searchbar {
    margin-top: #{$search-bar-offset-sm * -1};

    @include breakpoint.min-width(breakpoint.$breakpoint--9) {
      margin-top: #{$search-bar-offset-lg * -1};
    }
  }

  &__listing-controls {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  &__loading {
    animation: rotation 1s infinite ease-in-out;
    margin: 0 auto;
  }

  @keyframes rotation {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(359deg);
    }
  }
}
</style>
